import Head from 'next/head';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import isEmpty from 'lodash/isEmpty';
import QuizLayout from '@cont/Quiz/Layout';
import BranchDeepLinksScript from '@Analytics/BranchDeepLinksScript';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import {
  fetchQuizMinimalData,
  fetchActiveSessionData
} from '@actions/quizTaking';
import { getQuizDetails } from '@selectors/quizTaking';
import { getMe, getMeUid } from '@selectors/auth';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import { redirectToRoute } from '@utils/route';
import setupInitialProps from '@utils/setupInitialProps';
import fetchIsGoalGTP, {
  constantsAPIKey,
  fetchConstants
} from '@api/fetchers/constants';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { hasActiveSubscriptionToGoal } from '@models/Me';
import { isRevampedTestVersion2 } from '@cont/Quiz/utils';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useFollowGoal from '@api/hooks/useFollowGoal';
import { useEffect } from 'react';

const QuizMetaInfo = ({ goalUID }) => {
  const dispatch = useDispatch();
  const { query } = useRouter();
  const { quizUID } = query;

  const meUID = useSelectorWithShallowEqual(getMeUid);
  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));

  const name = `${quizDetails?.title} | Unacademy`;
  const description = quizDetails?.description;
  const canonicalURL =
    quizDetails?.slug && quizDetails?.uid
      ? `https://unacademy.com/quiz/${quizDetails.slug}/${quizDetails.uid}/`
      : null;

  const openGraph = {
    title: name,
    description,
    url: canonicalURL,
    images: [
      {
        url:
          quizDetails?.ogImage ||
          quizDetails?.thumbnail ||
          quizDetails?.coverPhoto
      }
    ]
  };

  useFirstRenderSkip(() => {
    dispatch(fetchQuizMinimalData(quizUID));
  }, [meUID]);

  useFirstRenderSkip(() => {
    if (quizDetails.user_active_session) {
      dispatch(
        fetchActiveSessionData(quizUID, quizDetails.user_active_session)
      );
    }
  }, [quizDetails.user_active_session]);

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { followCurrentGoal } = useFollowGoal(goalUID);
  useEffect(() => {
    if (isLoggedIn) {
      followCurrentGoal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <>
      <NextSeo
        title={name}
        description={description}
        canonical={canonicalURL}
        openGraph={openGraph}
      />
      <Head>
        <title>{name}</title>
        {canonicalURL && <link rel="canonical" href={canonicalURL} />}
      </Head>
      <BranchDeepLinksScript />
    </>
  );
};

QuizMetaInfo.pageInfo = {
  name: 'Test Details Page',
  isMWebSupported: true,
  layoutOptions: {
    hideIntercom: true,
    hideFreshbot: true,
    disableDarkMode: true
  }
};

QuizMetaInfo.getInitialProps = setupInitialProps(
  async ({ store, query, isServer, res }) => {
    const { quizUID } = query;

    await store.dispatch(fetchQuizMinimalData(quizUID));

    const quizDetails = getQuizDetails(quizUID)(store.getState());
    const goalUID = quizDetails.goal_uid;
    const state = store.getState();
    const meData = getMe(state);
    const isPlusUser = hasActiveSubscriptionToGoal(meData, goalUID);
    const isGTP = await fetchIsGoalGTP(goalUID, store);

    if (!isPlusUser && isGTP)
      return redirectToRoute(isServer, res, `/goal/-/${goalUID}`);

    if (isEmpty(quizDetails)) {
      return redirectToRoute(isServer, res, '/');
    }
    const constants = await fetchConstants();
    const isRevampedTest = isRevampedTestVersion2(goalUID, constants);

    const { user_active_session } = getQuizDetails(quizUID)(store.getState());

    if (user_active_session) {
      await store.dispatch(
        fetchActiveSessionData(quizUID, user_active_session, isRevampedTest)
      );
    }
    return {
      goalUID,
      globalFallbackData: {
        [constantsAPIKey]: constants // pragma: allowlist secret
      }
    };
  },

  QuizMetaInfo.pageInfo
);

QuizMetaInfo.Layout = QuizLayout;

export default QuizMetaInfo;
QuizMetaInfo.Wrapper = PageReduxProvider;
